import React, { createContext, ReactNode, useContext, useEffect } from "react";

import { Configuration } from "../openapi-generated-client/configuration";
import {AccountControllerApi, ArtistControllerApi, AssetControllerApi, BookmarkControllerApi, DownloadControllerApi, KeywordControllerApi, LikeControllerApi, LoginResponseDTO, OverviewControllerApi, PageControllerApi, PayOutControllerApi, PieceControllerApi, PurchaseControllerApi, ReportControllerApi, ScoreControllerApi, SettingsControllerApi} from "../openapi-generated-client/api";
import Cookies from "universal-cookie";
import { GlobalWorkerOptions } from "pdfjs-dist";

GlobalWorkerOptions.workerSrc = window.location.origin + "/pdf.worker.min.mjs";

interface AppContextType {
    configuration:     Configuration;
    
    pieceController: PieceControllerApi;
    accountController: AccountControllerApi;
    bookmarkController: BookmarkControllerApi;
    likeController: LikeControllerApi;
    overviewController: OverviewControllerApi;
    downloadController: DownloadControllerApi;
    settingsController: SettingsControllerApi;
    artistController: ArtistControllerApi;
    scoreController: ScoreControllerApi;
    pageController: PageControllerApi;
    purchaseController: PurchaseControllerApi;
    payoutController: PayOutControllerApi;
    keywordController: KeywordControllerApi;
    assetController: AssetControllerApi;
    reportController: ReportControllerApi;

    userData: LoginResponseDTO | undefined;
    sessionId: string;
    setUserData: (userData: LoginResponseDTO | undefined) => void;
    isLoggedIn: boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
    children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {

    const cookies = new Cookies();
    
    const COOKIE_EXPIRE = 1000 * 60 * 60 * 24 * 60; // 60 days

    const [isInitializing, setIsInitializing] = React.useState(true);

    const configuration = new Configuration();
    const pieceController = new PieceControllerApi(configuration);
    const accountController = new AccountControllerApi(configuration);
    const bookmarkController = new BookmarkControllerApi(configuration);
    const likeController = new LikeControllerApi(configuration);
    const overviewController = new OverviewControllerApi(configuration);
    const downloadController = new DownloadControllerApi(configuration);
    const settingsController = new SettingsControllerApi(configuration);
    const artistController = new ArtistControllerApi(configuration);
    const scoreController = new ScoreControllerApi(configuration);
    const pageController = new PageControllerApi(configuration);
    const purchaseController = new PurchaseControllerApi(configuration);
    const payoutController = new PayOutControllerApi(configuration);
    const keywordController = new KeywordControllerApi(configuration);
    const assetController = new AssetControllerApi(configuration);
    const reportController = new ReportControllerApi(configuration);
    const [userData, setUserData] = React.useState<LoginResponseDTO | undefined>(undefined);

    function updateUserData(userData: LoginResponseDTO | undefined) {
        setUserData(userData);
        if(userData === undefined) {
            cookies.remove("userData", {path: "/"});
            return;
        }
        cookies.set("userData", userData, {path: "/", expires: new Date(Date.now() + COOKIE_EXPIRE)});
    }

    //Load user data from cookies on initialization
    useEffect(() => {
        const storedUserData = cookies.get("userData") as LoginResponseDTO | undefined;
        if(storedUserData !== undefined) {
            setUserData(storedUserData);
        }
        setIsInitializing(false);
    }, []);

    //Check session id when userData changes
    useEffect(() => {
        if(userData != undefined && userData.sessionId != undefined) {
            accountController.check(userData.sessionId).catch(() => {
                console.log("Invalid session, logging out");
                updateUserData(undefined);
            })
        }
    }, [userData])

    const value: AppContextType = {
        configuration,

        pieceController,
        accountController,
        bookmarkController,
        likeController,
        overviewController,
        downloadController,
        settingsController,
        artistController,
        scoreController,
        pageController,
        purchaseController,
        payoutController,
        keywordController,
        assetController,
        reportController,

        userData,
        sessionId: userData?.sessionId || "",
        setUserData: updateUserData,
        isLoggedIn: userData !== undefined && userData.sessionId !== undefined
    };

    if(isInitializing) {
        return <></>;
    }

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );

}

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};

export default AppContext;
export type {AppContextType};